import { useEffect, useMemo, useState } from "react";
import { api, apiErrToStr, fmtDate } from "@/lib/api";
import { Globe2, RefreshCw, Save, ShieldAlert } from "lucide-react";

const DEFAULT_STATE = {
  enabled: false,
  mode: "allow_selected",
  countries: [],
  countries_available: [],
  runtime: { counters: {}, timer_active: false },
  updated_at: "",
  updated_by: "",
};

export default function GeoIP() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/geoip/settings");
      setState({ ...DEFAULT_STATE, ...data, runtime: data.runtime || DEFAULT_STATE.runtime });
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredCountries = useMemo(() => {
    const term = String(filter || "").trim().toLowerCase();
    const rows = state.countries_available || [];
    if (!term) return rows;
    return rows.filter((row) =>
      String(row.code || "").toLowerCase().includes(term)
      || String(row.name || "").toLowerCase().includes(term)
    );
  }, [filter, state.countries_available]);

  const selectedSet = useMemo(() => new Set(state.countries || []), [state.countries]);
  const dropCounter = state.runtime?.counters?.["Voxyra GeoIP SIP-TFTP"] || { packets: 0, bytes: 0 };

  const toggleCountry = (code) => {
    setState((current) => {
      const set = new Set(current.countries || []);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      return { ...current, countries: Array.from(set).sort() };
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        enabled: !!state.enabled,
        mode: state.mode || "allow_selected",
        countries: Array.from(new Set(state.countries || [])).sort(),
      };
      const { data } = await api.put("/geoip/settings", payload);
      setState({ ...DEFAULT_STATE, ...data, runtime: data.runtime || DEFAULT_STATE.runtime });
      setMessage("Politica GeoIP atualizada.");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const refreshRuntime = async () => {
    setRefreshing(true);
    setError("");
    setMessage("");
    try {
      const { data } = await api.post("/geoip/refresh");
      setState((current) => ({ ...current, runtime: data.runtime || current.runtime }));
      setMessage("Bases GeoIP atualizadas.");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="geoip-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Controle de acesso geográfico</div>
          <h1 className="text-2xl font-semibold tracking-tight">GeoIP</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1">Permite restringir SIP e TFTP por país com atualização da política no firewall interno.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="sbc-btn" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
          <button className="sbc-btn" onClick={refreshRuntime} disabled={refreshing}>
            <Globe2 size={14} className={refreshing ? "animate-spin" : ""} /> Atualizar bases
          </button>
          <button className="sbc-btn sbc-btn-primary" onClick={save} disabled={saving}>
            <Save size={14} className={saving ? "animate-pulse" : ""} /> Salvar
          </button>
        </div>
      </div>

      {error && <div className="text-xs font-mono text-[color:var(--accent-red)] border border-[rgba(239,68,68,0.25)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">! {error}</div>}
      {message && <div className="text-xs font-mono text-[color:var(--accent-green)] border border-[rgba(16,185,129,0.25)] bg-[rgba(16,185,129,0.08)] px-3 py-2 rounded-sm">{message}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">GeoIP</div>
          <div className="text-2xl font-mono mt-2">{state.enabled ? "ATIVO" : "INATIVO"}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Modo: {state.mode === "allow_selected" ? "permitir selecionados" : state.mode}</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Timer</div>
          <div className="text-2xl font-mono mt-2">{state.runtime?.timer_active ? "ATIVO" : "PARADO"}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Atualização periódica das bases</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Pacotes bloqueados</div>
          <div className="text-2xl font-mono mt-2">{Number(dropCounter.packets || 0)}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Contador nftables SIP/TFTP</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Ultima alteração</div>
          <div className="text-sm font-mono mt-2">{fmtDate(state.updated_at)}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">{state.updated_by || "-"}</div>
        </div>
      </div>

      <div className="sbc-card p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Política de países</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">Quando ativa, apenas os países selecionados podem atingir os serviços protegidos pelo GeoIP.</div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!state.enabled} onChange={(e) => setState((current) => ({ ...current, enabled: e.target.checked }))} />
            Ativar GeoIP
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] gap-4">
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block">Modo</label>
            <select className="sbc-input" value={state.mode || "allow_selected"} onChange={(e) => setState((current) => ({ ...current, mode: e.target.value }))}>
              <option value="allow_selected">Permitir países selecionados</option>
            </select>
            <div className="text-xs text-[color:var(--text-muted)]">Selecionados: {(state.countries || []).length}</div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block">Filtrar países</label>
            <input className="sbc-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Ex: BR ou Brazil" />
          </div>
        </div>

        <div className="border border-[color:var(--border-default)] rounded-sm overflow-hidden">
          <div className="max-h-[420px] overflow-auto">
            <table className="sbc-table">
              <thead>
                <tr>
                  <th>Liberar</th>
                  <th>Código</th>
                  <th>País</th>
                </tr>
              </thead>
              <tbody>
                {!filteredCountries.length && <tr><td colSpan="3" className="text-center py-8 text-[color:var(--text-muted)]">Nenhum país encontrado.</td></tr>}
                {filteredCountries.map((country) => (
                  <tr key={country.code}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(country.code)}
                        onChange={() => toggleCountry(country.code)}
                      />
                    </td>
                    <td className="font-mono">{country.code}</td>
                    <td>{country.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="sbc-card p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert size={18} className="text-[color:var(--accent-amber)] mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Observação operacional</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">
              O menu GeoIP é restrito ao super admin. A política é aplicada no firewall interno e interfere no acesso SIP/TFTP dos aparelhos e troncos externos.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
